import { isFuture, isPast } from 'date-fns';
import { flatten, partition, sortBy } from 'lodash';

import {
  Maybe,
  Product,
  ProductCategory as GraphQLProductCategory,
  ProductDriveType as GraphQLProductDriveType,
  ServiceReminder as GraphQLServiceReminder,
} from '@phoenix/team-m/dashboard/frontend/domain/generated/graphql';

import { ProductCard, ProductCategory, ProductDriveType, ServiceReminder } from './product.model';

export const mapProductCategory = (productCategory?: Maybe<GraphQLProductCategory>): ProductCategory | undefined =>
  productCategory
    ? {
        id: productCategory.id || undefined,
        name: productCategory.name,
        categoryGroup: {
          name: productCategory?.categoryGroup?.name || '',
        },
      }
    : undefined;

export const mapProductDriveType = (productDriveType?: Maybe<GraphQLProductDriveType>): ProductDriveType | undefined =>
  productDriveType ? { name: productDriveType.name } : undefined;

export const mapToProductCard = ({ serialNumber, serviceReminder, ...product }: Product): ProductCard => {
  return {
    id: product.id!,
    productCategory: mapProductCategory(product.productCategory),
    serialNumber: serialNumber ?? undefined,
    productDriveType: mapProductDriveType(product.productDriveType),
    customName: product.customName || '',
    productName: product.productName || '',
    image: { src: product.imageSrc ?? undefined, isFallback: product.isFallbackImage ?? false },
    ...(serialNumber &&
      serviceReminder && {
        serviceReminder: mapServiceReminder(serviceReminder),
      }),
  };
};

export const mapServiceReminder = (serviceReminder: Partial<GraphQLServiceReminder>): ServiceReminder => ({
  ...(serviceReminder.nextServiceDate && {
    nextServiceDate: serviceReminder.nextServiceDate,
    isNextServiceDateDue: isPast(new Date(serviceReminder.nextServiceDate)),
  }),
  ...(serviceReminder.reminderDate
    ? { reminderDate: serviceReminder.reminderDate, hasFutureReminderDate: isFuture(new Date(serviceReminder.reminderDate)) }
    : { hasFutureReminderDate: false }),
});

const sortByServiceDate = (products: ProductCard[]) => sortBy(products, 'serviceReminder.nextServiceDate');

export const sortByServiceReminder = (products: ProductCard[]) => {
  const [withServiceReminder, withoutServiceReminder] = partition(products, 'serviceReminder');
  const [dueServiceDate, noDueServiceDate] = partition(sortByServiceDate(withServiceReminder), 'serviceReminder.isNextServiceDateDue');

  return [
    ...flatten(partition(dueServiceDate, 'serviceReminder.hasFutureReminderDate').reverse()),
    ...flatten(partition(noDueServiceDate, 'serviceReminder.hasFutureReminderDate').reverse()),
    ...withoutServiceReminder,
  ];
};

export const mapProductPowerEventData = (name: string): string => {
  const CORDLESS = 'cordless',
    ELECTRIC = 'electric',
    MANUAL = 'manual',
    GASOLINE = 'gasoline';

  const patterns = {
    '^.{2}A .*': CORDLESS,
    '^.{2}E .*': ELECTRIC,
    '^RE .*': ELECTRIC,
    '^SE .*': ELECTRIC,
    '^SG .*': MANUAL,
    '^KG .*': MANUAL,
    '^RMI .*': CORDLESS,
    '^.{2} .*': GASOLINE,
  };

  for (const pattern in patterns) {
    if (new RegExp(pattern).test(name)) {
      return patterns[pattern];
    }
  }

  return `n/a - ${name}`;
};
